<template>
<span id="manual-invoice-detail-section" v-if="invoice.type === manualInvoiceType">
  <i
    class="el-icon-search header-info-dialog-value icon-action"
    :id="`btn-invoice-detail-${invoice.id}`"
    @click="handleOpenDialog"
    size="mini" icon="el-icon-plus" v-if="showIconGrid"
  ></i>
  <el-dialog
    :close-on-click-modal="false"
    :title=" $t('invoices.manualInvoice')"
    :visible.sync="dialogVisible"
    width="85%"
    id="manual-invoice-detail-dialog"
  >
    <span slot="title">
      <el-row>
        <el-col :span="12">
          <span class="el-dialog__title">
            {{ $t('invoices.manualInvoice') }}
          </span>
        </el-col>
        <el-col :span="11" class="text-align-end">
          <el-button
            id="edit-manual-invoice-btn"
            plain
            v-if="canEditInvoice && !editing"
            @click="toggleEditable"
            size="mini" class="button-action-default"
          >
            {{ $t('invoices.edit') }}
          </el-button>
        </el-col>
      </el-row>
    </span>

    <el-form :model="editForm" :rules="editFormRules" ref="editForm">
      <el-row :gutter="10">
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('invoices.invoiceNum') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ invoice.number }} </span>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.date') }}</label>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <span class="span-small-list"> {{ invoice.date | moment('MM/DD/YYYY') }} </span>
        </el-col>
        <el-col :span="8" v-show="this.editing">
          <el-form-item :label="$t('invoices.date')" prop="date" ref="date">
            <el-date-picker
              v-model="editForm.date"
              format="MM/dd/yyyy"
              :clearable="false"
              type="date"
            ></el-date-picker>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('invoices.posted') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ invoice.posted ? $t('common.yes') : $t('common.no') }} </span>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="4" v-show="!this.editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.billTo') }}</label>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <el-tooltip class="item" effect="dark" :enterable="false" :content="invoice.billToAccountName + ' - ' + invoice.billToAccountNumber" placement="top-start">
            <span class="span-small-list"> {{ invoice.billToAccountName }} - {{ invoice.billToAccountNumber }} </span>
          </el-tooltip>
        </el-col>
        <el-col :span="8" v-show="this.editing">
          <el-form-item :label="$t('invoices.billTo')" prop="billToAccountId" ref="billToAccountId">
            <autocomplete
              :model.sync="editForm.billToAccountId"
              labelFieldLastWithDash="number"
              popperAppendToBody
              :url="urlBillTo"
              labelField="name"
              valueField="id"
              ref="billToAccount"
              id="billToAccount"
            ></autocomplete>
          </el-form-item>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.customer') }}</label>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <el-tooltip
            v-if="this.hasConsigneeData()"
            class="item" effect="dark"
            :enterable="false"
            :content="invoice.consigneeAccountName + ' - ' + invoice.consigneeAccountNumber"
            placement="top-start"
          >
            <span class="span-small-list"> {{ invoice.consigneeAccountName }} - {{ invoice.consigneeAccountNumber }} </span>
          </el-tooltip>
        </el-col>
        <el-col :span="8" v-show="this.editing">
          <el-form-item :label="$t('invoices.customer')" prop="consigneeAccountId" ref="consigneeAccountId">
            <autocomplete
              :model.sync="editForm.consigneeAccountId"
              labelFieldLastWithDash="number"
              popperAppendToBody
              :url="urlConsignee"
              labelField="name"
              valueField="id"
              ref="consigneeAccount"
            ></autocomplete>
          </el-form-item>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.internalAdjustment') }}</label>
        </el-col>
        <el-col :span="4" v-show="!this.editing">
          <span class="span-small-list"> {{ invoice.internalAdjustment ? $t('common.yes') : $t('common.no') }} </span>
        </el-col>
        <el-col :span="8" v-show="this.editing">
          <el-form-item :label="$t('invoices.internalAdjustment')" prop="internalAdjustment" ref="internalAdjustment">
            <el-select v-model="editForm.internalAdjustment">
              <el-option :label="yes.label" :value="yes.value"></el-option>
              <el-option :label="no.label" :value="no.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="4" v-show="!editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.incidentNum') }}</label>
        </el-col>
        <el-col :span="4" v-show="!editing">
          <span class="span-small-list"> {{ invoice.caseNumber }} </span>
        </el-col>
        <el-col :span="8" v-show="editing">
          <el-form-item :label="$t('invoices.incidentNum')" prop="caseNumber">
            <el-input
              v-model="editForm.caseNumber"
              v-alphanumeric-validation
              maxlength="20"
              clearable
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4" v-show="!editing">
          <label class="margin-bottom-small-list">{{ $t('invoices.loadNum') }}</label>
        </el-col>
        <el-col :span="4" v-show="!editing">
          <span class="span-small-list"> {{ invoice.loadNumber }} </span>
        </el-col>
        <el-col :span="8" v-show="editing">
          <el-form-item :label="$t('invoices.loadNum')" prop="loadNum">
            <el-input
              v-model="editForm.loadNumber"
              v-alphanumeric-validation
              maxlength="50"
              clearable
            ></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('invoices.created') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ invoice.createdTime | moment('MM/DD/YYYY HH:mm') }} </span>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="4">
          <label class="margin-bottom-small-list">{{ $t('invoices.createdBy') }}</label>
        </el-col>
        <el-col :span="4">
          <span class="span-small-list"> {{ invoice.createdByUsername }} </span>
        </el-col>
        <el-col :span="4" v-show="!invoice.active">
          <label class="margin-bottom-small-list">{{ $t('invoices.deleted') }}</label>
        </el-col>
        <el-col :span="4" v-show="!invoice.active" v-if="invoice.deletedTime">
          <span class="span-small-list"> {{ invoice.deletedTime | moment('MM/DD/YYYY HH:mm') }} </span>
        </el-col>
        <el-col :span="4" v-show="!invoice.active">
          <label class="margin-bottom-small-list">{{ $t('invoices.deletedBy') }}</label>
        </el-col>
        <el-col :span="4" v-show="!invoice.active">
          <span class="span-small-list"> {{ invoice.deletedByUsername }} </span>
        </el-col>
      </el-row>
    </el-form>
    <div id="edit-manual-invoice-dialog-footer" class="dialog-footer form-vertical-item flex-reverse-direction"
         v-if="canEditInvoice" v-show="editing">
      <el-button type="primary" id="form-save-button" @click="handleUpdate()"
                 size="mini">{{ $t('common.save') }}</el-button>
      <el-button id="form-cancel-button" @click="editing = false" size="mini">{{ $t('common.cancel') }}</el-button>
    </div>

    <el-row class="margin-t-20">
      <el-col :span="12">
        <span class="title-list invoice-detail-table-title">{{ $t('invoices.invoiceDetails') }}</span>
      </el-col>
      <el-col :span="12" class="text-align-end" v-if="canEditInvoice" v-show="!editing">
         <add-manual-invoice-detail-dialog :invoice="invoice" @updateDetails="refresh()"/>
      </el-col>
    </el-row>

    <el-form :model="editDetailForm" :rules="editDetailFormRules" ref="editDetailForm" @submit.native.prevent>
      <el-table
        :data="details"
        v-loading="searching"
        id="invoice-details-data-list"
        size="mini"
        tooltip-effect="dark"
        class="dark-blue-table margin-t-20"
        :empty-text="$t('invoices.invoiceHasNoDetails')"
        stripe
      >
      <el-table-column
        prop="chargeCode"
        :label="$t('invoices.list.details.chargeCode')"
        :min-width="10"
        align="center">
      </el-table-column>
      <el-table-column
        :label="$t('invoices.list.details.rate')"
        :min-width="10"
        align="center"
      >
        <template slot-scope="scope">
          <template v-if="scope.row.edit">
            <el-form-item prop="rate">
              <el-input-number
                v-model="editDetailForm.rate"
                v-numeric-allow-negative-validation
                :controls="false"
                :precision="2"
                @focus="$event.target.select()"
                :min="-9999.99" :max="9999.99"
                size="mini"
                @change="calculateAmount(scope.row)"
                class="inline-input"
                id="edit-form-rate"
                ref="rate"
              ></el-input-number>
            </el-form-item>
          </template>
          <template v-else>
            <template v-if="scope.row.unitOfMeasure === unitOfMeasureOptions.PERCENTAGE">
              {{ scope.row.rate }} %
            </template>
            <template v-else>
              {{ formatNegativeMoney(scope.row.rate) }}
            </template>
          </template>
      </template>
      </el-table-column>
      <el-table-column
        :label="$t('invoices.list.details.quantity')"
        :min-width="10"
        align="center"
      >
        <template slot-scope="scope">
          <template v-if="scope.row.edit">
            <el-form-item prop="quantity">
              <el-input-number
                v-model="editDetailForm.quantity"
                v-numeric-validation
                :controls="false"
                :precision="2"
                @focus="$event.target.select()"
                :min="0" :max="9999.99"
                size="mini"
                @change="calculateAmount(scope.row)"
                class="inline-input"
                id="edit-form-quantity"
                ref="quantity"
              ></el-input-number>
            </el-form-item>
          </template>
          <template v-else>
            <span v-if="scope.row.unitOfMeasure === unitOfMeasureOptions.PERCENTAGE"> {{ scope.row.quantity | currency }} </span>
            <span v-else> {{ scope.row.quantity }} </span>
          </template>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('invoices.list.details.description')"
        :min-width="30"
        align="left"
        header-align="center"
      >
        <template slot-scope="scope">
          <template v-if="scope.row.edit">
            <el-form-item class="input-description" prop="description">
              <el-input
                v-model="editDetailForm.description"
                v-alphanumeric-validation
                :controls="false"
                @focus="$event.target.select()"
                size="mini"
                maxlength="100"
                class="inline-input"
                id="edit-form-description"
                ref="description"
              ></el-input>
            </el-form-item>
          </template>
          <template v-else>
          <el-tooltip class="item" effect="dark" :content="scope.row.description" placement="top">
            <span> {{ scope.row.description }} </span>
          </el-tooltip>
          </template>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('invoices.list.details.amount')"
        :min-width="10"
        align="center">
        <template slot-scope="scope">
          <span> {{ formatNegativeMoney(scope.row.totalRate) }} </span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('common.options')"
        :min-width="8"
        align="center">
          <template slot-scope="scope">
            <el-tooltip
              v-if="canEditInvoice && !editingDetail"
              class="item el-icon-edit-tooltip"
              effect="dark"
              :enterable="false"
              :content="$t('invoices.details.edit')"
              placement="top-start"
            >
              <i class="el-icon-edit icon-action" @click="handleEditDetail(scope.row)"></i>
            </el-tooltip>
            <template v-else-if="scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdateDetail(scope.row)"></el-button>
              <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEditDetail(scope.row)"></el-button>
            </template>
          <el-tooltip
            v-if="canEditInvoice && !editingDetail"
            class="item"
            effect="dark"
            :enterable="false"
            :content="$t('common.delete')"
            placement="top-start">
            <i class="el-icon-delete icon-action" @click="handleDelete(scope.row)"></i>
          </el-tooltip>
          </template>
      </el-table-column>
    </el-table>
  </el-form>

    <div class="total-amount-footer">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('invoices.totalAmount') }}</span>
         {{ formatNegativeMoney(invoice.totalAmount) }}
      </span>
    </div>
  </el-dialog>
</span>
</template>

<script>
import { Message } from 'element-ui';
import constants from '@/utils/constants';
import apiConstants from '@/utils/apiConstants';
import Autocomplete from '@/components/Autocomplete';
import AddManualInvoiceDetailDialog from './AddManualInvoiceDetailDialog';

export default {
  name: 'ManualInvoiceDetailDialog',
  components: { AddManualInvoiceDetailDialog, Autocomplete },
  props: {
    invoice: {
      type: Object,
      require: true
    },
    showIconGrid: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    manualInvoiceType() {
      return constants.INVOICE.TYPES[2].value;
    },
    canEditInvoice() {
      return !this.invoice.posted && this.invoice.active && this.$can(this.permissions.BILLING.EDIT_INVOICE);
    }
  },
  data() {
    return {
      editing: false,
      editingDetail: false,
      details: [],
      dialogVisible: false,
      searching: false,
      yes: constants.STATUS_BOOLEAN.yes,
      no: constants.STATUS_BOOLEAN.no,
      editForm: {
        id: null,
        date: null,
        billToAccountId: null,
        consigneeAccountId: null,
        internalAdjustment: null,
        caseNumber: null,
        loadNumber: null,
      },
      editDetailForm: {
        id: null,
        quantity: null,
        rate: null,
        description: null,
        unitOfMeasure: null,
        total: null,
        invoiceId: null,
        invoiceType: null
      },
      editFormRules: {
        date: [
          {
            required: true,
            message: this.$t('invoices.manual.error.dateRequired'),
            trigger: 'blur'
          }
        ],
        billToAccountId: [
          {
            required: true,
            message: this.$t('invoices.manual.error.billToAccountRequired'),
            trigger: 'submit'
          }
        ],
      },
      editDetailFormRules: {
        description: [
          {
            required: true,
            message: this.$t('invoices.manual.error.descriptionRequired'),
            trigger: 'blur'
          }
        ],
        rate: [
          {
            required: true,
            message: this.$t('invoices.manual.error.rateRequired'),
            trigger: 'blur'
          }
        ],
        quantity: [
          {
            required: true,
            message: this.$t('invoices.manual.error.quantityRequired'),
            trigger: 'blur'
          }
        ]
      },
      searchParams: {
        invoiceId: 0,
        invoiceType: 0
      },
      urlBillTo: `${apiConstants.END_POINTS.ACCOUNTS.ALL_BY_TYPE.replace('{accountTypeId}', '0')}/1`,
      urlConsignee: apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE,
      urlStates: apiConstants.END_POINTS.ROUTING.STATES,
      unitOfMeasureOptions: constants.INVOICE.RATE_UNIT_OF_MEASURE
    };
  },
  methods: {
    toggleEditable() {
      this.$refs.editForm.resetFields();
      this.editForm.id = this.invoice.id;
      this.editForm.date = this.invoice.date;
      this.editForm.billToAccountId = this.invoice.billToAccountId;
      this.editForm.consigneeAccountId = this.invoice.consigneeAccountId;
      this.editForm.internalAdjustment = this.invoice.internalAdjustment;
      this.editForm.caseNumber = this.invoice.caseNumber;
      this.editForm.loadNumber = this.invoice.loadNumber;
      this.$nextTick(() => {
        this.$refs.billToAccount.setLocalModel(`${this.invoice.billToAccountName} - ${this.invoice.billToAccountNumber}`);
        if (this.hasConsigneeData()) {
          this.$refs.consigneeAccount.setLocalModel(`${this.invoice.consigneeAccountName} - ${this.invoice.consigneeAccountNumber}`);
        }
      });
      this.editing = true;
    },
    handleUpdate() {
      this.$refs.editForm.validate((valid) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.$store
            .dispatch('invoices/updateInvoice', this.editForm).then((response) => {
              this.$store.dispatch('invoices/search', {});
              this.editing = false;
              this.$message.success(response.message);
            }).finally(() => loading.close());
        }
      });
    },
    handleOpenDialog() {
      this.dialogVisible = true;
      this.searchParams.invoiceId = this.invoice.id;
      this.searchParams.invoiceType = this.invoice.type;
      this.searchDetail();
    },
    searchDetail() {
      this.searching = true;
      this.$store.dispatch('invoices/details', this.searchParams).then(
        (response) => {
          this.details = response.data;
        }
      ).finally(() => {
        this.searching = false;
      });
    },
    refresh() {
      if (!this.showIconGrid) {
        this.$store.dispatch('invoices/search', {});
      }
      this.searchDetail();
    },
    handleEditDetail(row) {
      row.edit = true;
      this.editingDetail = true;
      this.editDetailForm.id = row.id;
      this.editDetailForm.rate = row.rate;
      this.editDetailForm.quantity = row.quantity;
      this.editDetailForm.unitOfMeasure = row.unitOfMeasure;
      this.editDetailForm.description = row.description;
    },
    clearEdit() {
      this.editDetailForm.id = null;
      this.editDetailForm.rate = null;
      this.editDetailForm.quantity = null;
      this.editDetailForm.unitOfMeasure = null;
      this.editDetailForm.description = null;
      this.amountCalculated = 0;
    },
    cancelEditDetail(row) {
      this.clearEdit();
      row.edit = false;
      this.editingDetail = false;
    },
    // This calculation is Informative, Final Quantity and Amount is Calculated in Backend.
    calculateAmount(row) {
      // UNITS or CUBES
      if (this.editDetailForm.unitOfMeasure === this.unitOfMeasureOptions.CURRENCY) {
        this.amountCalculated = this.editDetailForm.rate * this.editDetailForm.quantity;
      } else if (this.editDetailForm.unitOfMeasure === this.unitOfMeasureOptions.PERCENTAGE) {
        this.amountCalculated = this.editDetailForm.quantity * (this.editDetailForm.rate / 100);
      } else {
        this.amountCalculated = 0;
      }
      row.totalRate = this.amountCalculated;
    },
    async handleUpdateDetail(row) {
      this.$refs.editDetailForm.validate(async (valid, validationsErrors) => {
        if (valid) {
          this.calculateAmount(row);

          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          try {
            this.editDetailForm.invoiceId = this.invoice.id;
            this.editDetailForm.invoiceType = this.invoice.type;
            const response = await this.$store.dispatch('invoices/editDetails', this.editDetailForm);
            this.$message.success(response.message);
            this.cancelEditDetail(row);
            this.invoice.totalAmount = response.data.totalAmount;
            this.refresh();
          } finally {
            loading.close();
          }
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    async handleDelete(row) {
      const confirm = await this.$confirm(
        this.$t('invoices.details.confirmDelete')
          .replace('[chargeCode]', row.chargeCode),
        { confirmButtonText: this.$t('common.delete'), dangerouslyUseHTMLString: true }
      ).catch(() => {
      });

      if (confirm) {
        const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
        try {
          const response = await this.$store.dispatch('invoices/deleteDetails', {
            invoiceId: this.invoice.id,
            type: this.invoice.type,
            id: row.id
          });
          this.$message.success(response.message);
          this.invoice.totalAmount = response.data.totalAmount;
          this.refresh();
        } finally {
          loading.close();
        }
      }
    },
    formatNegativeMoney(money) {
      if (money < 0) {
        return `(${this.$options.filters.currency(money).replace('-', '')})`;
      }
      return this.$options.filters.currency(money);
    },
    hasConsigneeData() {
      return this.invoice.consigneeAccountNumber != null && this.invoice.consigneeAccountName != null;
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
span#manual-invoice-detail-section {
  .el-form-item__label {
    width: 50%;
    text-align: right;
    padding-right: 10px;
    line-height: 1.15;
  }

  .el-form-item__content {
    width: 50%;
    white-space: nowrap;
    line-height: 18px;
  }

  .el-input-number {
    line-height: 18px;
  }

  .input-description .el-form-item__content {
    width: 100%;
  }

  .el-date-editor {
    width: 100%;

    .el-input__inner {
      padding-left: 30px;
    }

    .el-icon-date {
      line-height: 1.4;
    }
  }

  th {
    line-height: 1.15;

    .cell {
      line-height: 16px;
      height: 16px;
    }
  }

  .total-amount-footer {
    margin-top: 10px;
    margin-bottom: 10px;
    text-align: left;
  }

  input {
    font-size: 10px;
    height: 24px;
    padding-left: 5px;
    padding-right: 5px;
  }

  .el-icon-arrow-up {
    line-height: 27px;
  }

  .el-input__suffix {
    right: 0;

    .el-input__icon {
      line-height: 27px;
    }
  }

  .invoice-detail-table-title {
    margin-top: 5px;
    margin-bottom: 0;
  }
}
</style>
